import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Alert, Button, Descriptions, Divider, Modal, Progress, Tag, Typography } from "antd";
import * as React from "react";
import {
  getQuestProgressStatusLabel,
  getQuestTypeLabel,
  QuestProgressItem,
  QuestProgressStatus,
} from "src/hooks/affiliate";
import { datetime } from "src/utils";
import { getQuestDebugInfo } from "./questDebug";

const { Text, Paragraph } = Typography;

const statusColor: Record<string, string> = {
  [QuestProgressStatus.IN_PROGRESS]: "blue",
  [QuestProgressStatus.COMPLETED]: "green",
  [QuestProgressStatus.REWARDED]: "gold",
};

const formatDateTime = (value?: string | null) =>
  value ? datetime(value).format("DD/MM/YYYY HH:mm") : "-";

export type QuestDebugModalProps = {
  progress: QuestProgressItem;
};

export const QuestDebugModal = NiceModal.create(({ progress }: QuestDebugModalProps) => {
  const { visible, hide, remove } = useModal();
  const info = getQuestDebugInfo(progress.questType);
  const isStuck = progress.status === QuestProgressStatus.IN_PROGRESS;

  return (
    <Modal
      centered
      width={760}
      visible={visible}
      afterClose={remove}
      onCancel={hide}
      title={`รายละเอียดเควส: ${progress.questName}`}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button type="primary" onClick={hide}>
            ปิด
          </Button>
        </div>
      }
    >
      {/* สถานะปัจจุบันของ progress แถวนี้ */}
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="ประเภทเควส" span={2}>
          <Tag>{getQuestTypeLabel(progress.questType)}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {progress.questType}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="แคมเปญ" span={2}>
          <Tag color="blue">{progress.campaignCode}</Tag> {progress.campaignName}
        </Descriptions.Item>
        <Descriptions.Item label="ความคืบหน้า" span={2}>
          <Progress
            percent={progress.percent}
            size="small"
            format={() => `${progress.currentValue}/${progress.targetValue}`}
          />
        </Descriptions.Item>
        <Descriptions.Item label="สถานะ">
          <Tag color={statusColor[progress.status] || "default"}>
            {getQuestProgressStatusLabel(progress.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="อัปเดตล่าสุด">{formatDateTime(progress.updatedAt)}</Descriptions.Item>
        <Descriptions.Item label="วันที่สำเร็จ">
          {formatDateTime(progress.completedAt)}
        </Descriptions.Item>
        <Descriptions.Item label="วันที่รับรางวัล">
          {formatDateTime(progress.rewardedAt)}
        </Descriptions.Item>
      </Descriptions>

      {!info ? (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          message={`ไม่มีข้อมูลสำหรับประเภทเควสนี้ (${progress.questType})`}
        />
      ) : (
        <>
          <Divider orientation="left" plain style={{ marginTop: 20 }}>
            วิธีทำงาน
          </Divider>
          <Paragraph style={{ marginBottom: 8 }}>{info.howItWorks}</Paragraph>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="ทริกเกอร์เมื่อ">{info.trigger}</Descriptions.Item>
            <Descriptions.Item label="แหล่งข้อมูลหลัก">{info.sourceOfTruth}</Descriptions.Item>
            <Descriptions.Item label="การตั้งค่า">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {info.config.map((c) => (
                  <li key={c}>
                    <Text style={{ fontSize: 13 }}>{c}</Text>
                  </li>
                ))}
              </ul>
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" plain style={{ marginTop: 20 }}>
            เหตุผลที่ความคืบหน้าอาจไม่ขยับ
          </Divider>
          <Alert
            type={isStuck ? "info" : "success"}
            showIcon
            message={
              isStuck
                ? "เควสยังไม่สำเร็จ — เช็คสาเหตุที่เป็นไปได้ด้านล่าง"
                : "เควสสำเร็จแล้ว (ข้อมูลนี้ไว้อ้างอิงสาเหตุที่ทำให้ไม่ขยับ)"
            }
            description={
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {info.notTrigger.map((r) => (
                  <li key={r}>
                    <Text style={{ fontSize: 13 }}>{r}</Text>
                  </li>
                ))}
              </ul>
            }
          />
        </>
      )}
    </Modal>
  );
});

export default QuestDebugModal;
